import React, { useEffect, useState, useRef } from "react";
import { arrayOf, bool, shape } from "prop-types";
import { connect, useDispatch } from "react-redux";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import TextField from "pages/_components/fields/TextField";
import Button from "pages/_components/Button";
import { replace } from "connected-react-router";
import MainContainer from "pages/_components/MainContainer";
import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { Col, Row } from "react-bootstrap";
import { validatePhonePayway, validateCUIT } from "util/form";

import { ResponsiveHeader } from "pages/_components/header";
import Selector from "pages/_components/fields/formik/Selector";
import PhoneInput from "pages/_components/fields/PhoneInput";
import Notification from "pages/_components/Notification";
import PaywayTitle from "../_components/PaywayTitle";

import EnvironmentHelpSlide from "../_components/EnvironmentHelpSlide";
import EnvironmentStepper from "../_components/EnvironmentStepper";
import { useHistoricData } from "../hooks/useHistoricData";
import { usePhoneAreaCodesArg } from "../../../hooks/usePhoneAreaCodesArg";

const PAGE_ID = "StepSelectPaywayCategory";
const FORM_ID = "payway.select.category";

const StepSelectPaywayCategory = ({ isMobile, fetching, paywayCategories, paywayCategoryBackup }) => {
    const formRef = useRef(null);
    const dispatch = useDispatch();
    const [helpIsVisible, setHelpIsVisible] = useState(false);
    const [defaultPhoneNumber] = useState("");
    const [updatePhone, setUpdatePhone] = useState(false);
    const { argAreaCodes } = usePhoneAreaCodesArg();
    const [paywayEstablishmentTypes, setPaywayEstablishmentTypes] = useState([]);

    useEffect(() => {
        dispatch(environmentActions.restartEnvironmentStore());
        dispatch(environmentActions.getPaywayCategories());

        const establishmentTypes = configUtils.get("payway.establishmentTypes");
        if (paywayEstablishmentTypes && !paywayEstablishmentTypes.length) {
            setPaywayEstablishmentTypes(
                establishmentTypes
                    .split("|")
                    .map((elem) => ({ value: elem, label: i18nUtils.get(`payway.establishmentType.${elem}`) })),
            );
        }
    }, []);

    const noBackupData = !!paywayCategoryBackup?.fantasyName;
    const { getBackupDataByStepId } = useHistoricData(!noBackupData);

    const onHeaderBack = () => {
        getBackupDataByStepId("stepSelectPaywayTerminalType");
        dispatch(replace("/environment/stepSelectPaywayTerminalType"));
    };

    const requiredString = i18nUtils.get("forms.inputFile.massivePayments.invalid.required");

    useEffect(() => {
        if (formRef && !formRef.current) {
            return;
        }
        if (paywayCategoryBackup && !fetching) {
            formRef.current.setFieldValue("fantasyName", paywayCategoryBackup?.fantasyName || "");
            formRef.current.setFieldValue("phoneNumber", paywayCategoryBackup?.phoneNumber, false);
            formRef.current.setFieldValue("category", paywayCategoryBackup?.categoryId || null);
            formRef.current.setFieldValue("establishmentType", paywayCategoryBackup?.establishmentType || "");
            formRef.current.setFieldValue("wholesalerCuit", paywayCategoryBackup?.wholesalerCuit || "");
        }
    }, [fetching, paywayCategoryBackup]);

    const formikProps = {
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            fantasyName: paywayCategoryBackup?.fantasyName || "",
            phoneNumber: paywayCategoryBackup?.phoneNumber,
            category: paywayCategoryBackup?.categoryId || null,
            establishmentType: paywayCategoryBackup?.establishmentType || "",
            wholesalerCuit: paywayCategoryBackup?.wholesalerCuit || "",
        },
        validationSchema: () =>
            Yup.object().shape({
                fantasyName: Yup.string().required(requiredString),
                phoneNumber: validatePhonePayway(argAreaCodes),
                category: Yup.number().required(requiredString),
                establishmentType: Yup.string().required(requiredString),
                wholesalerCuit: Yup.string().when("establishmentType", {
                    is: "M",
                    then: Yup.string()
                        .required(requiredString)
                        .test(
                            "format-cuit",
                            i18nUtils.get("onboarding.step.verifyCUIT.cuit.error.invalidFormat"),
                            (value) => validateCUIT(value),
                        ),
                }),
            }),
        onSubmit: (values) => {
            const { fantasyName, phoneNumber, category, establishmentType, wholesalerCuit } = values;
            const params = {
                fantasyName,
                phoneNumber,
                categoryId: category,
                establishmentType,
                wholesalerCuit,
            };
            dispatch(environmentActions.setPaywayCategoryBackup(params));
            dispatch(environmentActions.savePaywayCategory(params));
        },
    };

    const submitIsAllowed = (allFormik) => {
        const { fantasyName, phoneNumber, category, establishmentType, wholesalerCuit } = allFormik.values;
        return (
            !!fantasyName &&
            !!phoneNumber &&
            !!category &&
            !!establishmentType &&
            (establishmentType === "M" ? !!wholesalerCuit : true)
        );
    };

    const onChangeEstablishmentType = (type) => {
        if (type === "T") {
            formRef.current.setFieldValue("wholesalerCuit", "");
        }
    };

    return (
        <>
            <ResponsiveHeader onClickBack={onHeaderBack} id="legalPerson.title" showTitle={isMobile} />
            <Notification scopeToShow="environment" />
            <MainContainer className="onboarding" showLoader={fetching}>
                <EnvironmentStepper isMobile={isMobile} page={PAGE_ID} />
                <div className="step-container">
                    <PaywayTitle handleHelp={() => setHelpIsVisible(true)} isDesktop={!isMobile} />
                    <div className="py-6" />
                    <div className="box box-border-soft payway-box">
                        <Formik {...formikProps} ref={formRef}>
                            {(allFormik) => (
                                <Form>
                                    <Row className="payway-terminals-form">
                                        <h2 className="payway-terminals-form-subtitle">
                                            {i18nUtils.get(
                                                "payway.category.step.companyData.label",
                                                "Datos de la empresa",
                                            )}
                                        </h2>
                                        <Col xs={12} md={5} mdOffset={1} className="payway-col-pl-30">
                                            <Field
                                                autoComplete="off"
                                                component={TextField}
                                                idForm={FORM_ID}
                                                maxLength={50}
                                                name="fantasyName"
                                                labelTextDefault={i18nUtils.get(
                                                    "payway.category.step.fantasyName.label",
                                                    "Nombre de fantasía",
                                                )}
                                                hidePlaceholder
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={1} className="payway-col-pl-30">
                                            <Field
                                                component={PhoneInput}
                                                idForm={FORM_ID}
                                                name="phoneNumber"
                                                country="AR"
                                                mobilePhone={defaultPhoneNumber || "54"}
                                                enableSearch={false}
                                                preferredCountries={["ar"]}
                                                labelTextDefault={i18nUtils.get(
                                                    "payway.terminals.phoneNumber",
                                                    "Teléfono",
                                                )}
                                                hintLabel={i18nUtils.get(
                                                    "",
                                                    "Debes incluir el código de área y luego el número.",
                                                )}
                                                updatePhone={updatePhone}
                                                phoneAlreadyUpdated={() => setUpdatePhone(false)}
                                            />
                                        </Col>
                                        <div className="py-3" />
                                        <h2 className="payway-terminals-form-subtitle">
                                            {i18nUtils.get(
                                                "payway.category.step.businessActivity.label",
                                                "Actividad del negocio",
                                            )}
                                        </h2>
                                        <Col xs={12} md={5} mdOffset={1} className="payway-col-pl-30">
                                            <Field
                                                component={Selector}
                                                idForm={FORM_ID}
                                                name="category"
                                                options={paywayCategories || []}
                                                label={`${i18nUtils.get(
                                                    "payway.select.category.selector.label",
                                                    "Rubro",
                                                )}`}
                                                placeholder={`${i18nUtils.get(
                                                    "onboarding.personalData.selectorPlaceholder",
                                                    "Elegir una opción",
                                                )}`}
                                            />
                                        </Col>
                                        <Col xs={12} md={5} mdOffset={1} className="payway-col-pl-30">
                                            <Field
                                                component={Selector}
                                                idForm={FORM_ID}
                                                name="establishmentType"
                                                options={paywayEstablishmentTypes || []}
                                                label={`${i18nUtils.get(
                                                    "payway.category.step.establishmentType.label",
                                                    "Tipo de adhesión",
                                                )}`}
                                                placeholder={`${i18nUtils.get(
                                                    "onboarding.personalData.selectorPlaceholder",
                                                    "Elegir una opción",
                                                )}`}
                                                onChange={onChangeEstablishmentType}
                                            />
                                        </Col>
                                        {allFormik.values.establishmentType === "M" && (
                                            <Col xs={12} md={5} mdOffset={6}>
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="wholesalerCuit"
                                                    labelTextDefault={i18nUtils.get(
                                                        "payway.category.step.wholesalerCuit.label",
                                                        "CUIT Beneficiario",
                                                    )}
                                                    hidePlaceholder
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                    <div className="py-6" />
                                    <Row className="form-actions no-print">
                                        <Col xs={12} md={4} mdOffset={4}>
                                            <Button
                                                className="btn-primary"
                                                label="global.continue"
                                                loading={false}
                                                type="submit"
                                                block
                                                disabled={!submitIsAllowed(allFormik)}
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </MainContainer>

            <EnvironmentHelpSlide
                slideIsVisible={helpIsVisible}
                closeSlide={() => setHelpIsVisible(false)}
                isMobile={isMobile}
                page={PAGE_ID}
            />
        </>
    );
};

StepSelectPaywayCategory.propTypes = {
    isMobile: bool.isRequired,
    fetching: bool.isRequired,
    paywayCategories: arrayOf(shape()).isRequired,
    paywayCategoryBackup: shape().isRequired,
};
const mapStateToProps = (state) => ({
    fetching: environmentSelectors.getFetching(state),
    paywayCategories: environmentSelectors.getPaywayCategories(state),
    paywayCategoryBackup: environmentSelectors.getPaywayCategoryBackup(state),
});

export default connect(mapStateToProps)(StepSelectPaywayCategory);
